import { defineComponent as _defineComponent } from 'vue'
import { dateField, radioField } from 'ah-common-lib/src/form/models';
import { minDate as minDateFn, ifTest, date } from 'ah-common-lib/src/form/validators';
import { getChildModel, setState, makeFormModel, submitForm } from 'ah-common-lib/src/form/helpers';
import { required } from '@vuelidate/validators';
import { format } from 'date-fns';
import { FormDefinition, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { computed, reactive, watch } from 'vue';
import { isValid, addBusinessDays } from 'date-fns';
import { PaymentType, Trade, paymentTypeLabels } from 'ah-api-gateways';
import LinkedToTradeDropdown from './LinkedToTradeDropdown.vue';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { useAhPaymentState } from '..';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'SchedulePaymentForm',
  props: {
    paymentType: null,
    executionDate: { default: undefined },
    readonly: { type: Boolean, default: false },
    minDate: { default: () => new Date() },
    paymentRail: { default: '' },
    trade: { default: null }
  },
  emits: ["update-execution-date", "update-payment-type", "update-trade"],
  setup(__props: any, { expose, emit }: { emit: ({
  /*
   * Emitted on form event, updating `executionDate`. If none is selected, `null` will be emmitted. Can be used with v-model
   */
  (e: 'update-execution-date', value: Date | null): void;
  /*
   * Emitted on form event, updating `paymentType`. Can be used with v-model
   */
  (e: 'update-payment-type', value: PaymentType): void;
  /*
   * Emitted on form event, updating `trade`. Can be used with v-model
   */
  (e: 'update-trade', value: Trade): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { paymentType?: PaymentType, executionDate: Date, readonly: boolean, minDate: Date, paymentRail: string, trade: Trade | null };





const paymentState = useAhPaymentState();
const onBehalfOfClient = useOnBehalfOf();

const state = reactive<{
  trade: Trade | null;
}>({
  trade: null,
});

const isPartnerUser = computed(() => !onBehalfOfClient.value && !paymentState.store.useAuthStore().isClientUser);

const paymentTypeOptions = computed(() => {
  let immediateLabel: string =
    `${paymentTypeLabels.IMMEDIATE} ` +
    (props.paymentRail && schedulePaymentDef?.form.schedule === PaymentType.IMMEDIATE ? `(${props.paymentRail})` : '');

  let paymentTypes = [{ label: immediateLabel, value: PaymentType.IMMEDIATE }];

  paymentTypes.push({ label: paymentTypeLabels.SCHEDULED, value: PaymentType.SCHEDULED });

  if (!isPartnerUser.value) {
    paymentTypes.push({
      label: paymentTypeLabels.TRADE_SEND_TO_BENEFICIARY,
      value: PaymentType.TRADE_SEND_TO_BENEFICIARY,
    });
  }

  return paymentTypes;
});

const schedulePaymentDateDef = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'schedulePaymentDate',
    fieldType: 'form',
    fields: [
      dateField(
        'executionDate',
        '',
        {
          errorMessages: {
            required: 'Date is required',
            minDate: () => 'Must be after ' + format(props.minDate, 'dd-MM-yyyy'),
          },
        },
        {
          date: ifTest(date, (val) => val instanceof Date),
          required: ifTest(required, (a) => !(a instanceof Date)),
          minDate: minDateFn(() => props.minDate),
        }
      ),
    ],
  }),
  validation: null,
});

const schedulePaymentDef = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'schedulePayment',
    fieldType: 'form',
    fields: [radioField('schedule', '', paymentTypeOptions.value, { required: true, clearable: false }, {})],
  }),
  validation: null,
});

const scheduleOptionNote = computed(() => {
  return (value: PaymentType) => {
    if (value === PaymentType.IMMEDIATE) {
      let date = addBusinessDays(new Date(), 1);
      return `Payment will be processed on ${formatDate(date, 'dd-MM-yyyy')}`;
    } else if (value === PaymentType.TRADE_SEND_TO_BENEFICIARY) return '';
    else return 'Payment will be scheduled on the selected date.';
  };
});

const isReadonly = computed(() => props.readonly !== false);

const valid = computed(() => {
  if (schedulePaymentDateDef.validation && schedulePaymentDef.form.schedule !== PaymentType.IMMEDIATE) {
    return !schedulePaymentDateDef.validation.$invalid;
  }
  return true;
});

watch(
  () => props.executionDate,
  () => {
    if (props.executionDate) {
      schedulePaymentDateDef.form.executionDate = props.executionDate;
    }
  },
  { immediate: true }
);

watch(
  () => props.paymentType,
  () => {
    if (props.paymentType) {
      schedulePaymentDef.form.schedule = props.paymentType;
    }
  },
  { immediate: true }
);

watch(
  () => props.readonly,
  () => {
    const scheduleForm = getChildModel(schedulePaymentDef.form, 'schedule')!;
    setState(scheduleForm, 'readonly', isReadonly.value);
  },
  { immediate: true }
);

watch(
  () => props.paymentRail,
  () => {
    if (props.paymentRail) {
      const scheduleForm = getChildModel(schedulePaymentDef.form, 'schedule')!;
      setState(scheduleForm, 'options', paymentTypeOptions);
    }
  },
  { immediate: true }
);

watch(
  () => props.trade,
  () => {
    state.trade = { ...props.trade, label: props.trade?.referenceNumber, value: props.trade?.id };
  },
  { immediate: true }
);
function onFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    if (event.model.$name === 'executionDate') {
      let out: Date | null;
      if (
        schedulePaymentDef.form.schedule === PaymentType.IMMEDIATE ||
        !isValid(new Date(schedulePaymentDateDef.form.executionDate))
      ) {
        out = null;
      } else {
        out = new Date(schedulePaymentDateDef.form.executionDate);
      }
      emit('update-execution-date', out);
    }
    if (event.model.$name === 'schedule') {
      emit('update-payment-type', schedulePaymentDef.form.schedule);
    }
  }
}

function touchForms() {
  if (schedulePaymentDateDef.validation) {
    submitForm(schedulePaymentDateDef.validation);
  }
}

function tradeSelected(trade: Trade) {
  state.trade = trade;
  emit('update-trade', trade);
}

expose({ valid: valid.value, touchForms });

return { __sfc: true,props, emit, paymentState, onBehalfOfClient, state, isPartnerUser, paymentTypeOptions, schedulePaymentDateDef, schedulePaymentDef, scheduleOptionNote, isReadonly, valid, onFormEvent, touchForms, tradeSelected, PaymentType, LinkedToTradeDropdown }
}

})